import React, { useState, useEffect, useMemo } from 'react'
import {
  GenericAnalyticsWidgetUIConfigType,
  AnalyticsMetricType,
  GenericAnalyticsWidgetDataRequestType,
  LineDistributionConfigType,
  AnalyticsEntityType
} from 'src/lib/types/analytics'
import GenericMetricData from './GenericMetricData'
import useSWR, { mutate } from 'swr'

import { staticOptions } from 'src/lib/core/api-client'
import ComponentLoader from 'src/components/common/loader-components/ComponentLoader'
import { makeMultipleRequests } from 'src/lib/core/api-methods'
import { GenericAnalyticsWidgetType } from 'src/lib/types/analytics'
import LineDistribution from './LineDistribution'

const GenericAnalyticsWidget: React.FC<GenericAnalyticsWidgetType> = ({
  uiConfig,
  requestConfig,
  customClass = '',
  analyticsEntity = AnalyticsEntityType.CART_ANALYTICS
}) => {
  const [widgetData, setWidgetData] = useState<GenericAnalyticsWidgetUIConfigType>(uiConfig)

  const { data: widgetResponses, isValidating } = useSWR(requestConfig.endpoints, makeMultipleRequests, staticOptions)

  useEffect(() => {
    mutate(requestConfig.endpoints)
  }, [requestConfig])

  const getWidgetData = (widgetResponse: any) => {
    if (analyticsEntity === AnalyticsEntityType.UPSELL_ANALYTICS) {
      return widgetResponse?.value?.data?.widgetData
    }
    return widgetResponse?.value?.data?.data ?? widgetResponse?.value?.data
  }

  useEffect(() => {
    if (!Boolean(widgetResponses) || Boolean(isValidating)) return
    setWidgetData(
      requestConfig.parser(
        widgetResponses?.map((widgetResponse: any) => getWidgetData(widgetResponse)),
        uiConfig
      )
    )
  }, [widgetResponses, isValidating])
  const style = widgetData?.id === 'cart-conversion' ? 'justify-between' : ''
  return (
    <div
      className={`hover:shadow-sm rounded-lg transition-all duration-200 ease-in-out h-fit`}
      style={{
        gridColumnStart: widgetData?.positionX ? widgetData?.positionX + 1 : 1,
        gridTemplateRows: 'auto'
      }}
    >
      <div
        className={`flex ${
          widgetData.orientation === 'VERTICAL' ? 'flex-col' : 'flex-row'
        } relative h-full gap-6 px-3 py-4 bg-white border rounded border-gray-light w-full ${customClass || ''}`}
      >
        {Boolean(widgetData?.title) && <h2 className='text-base font-medium text-carbon'>{widgetData?.title}</h2>}
        {Boolean(widgetData?.lineMetricList) && widgetData?.lineMetricList !== undefined && (
          <div className='flex flex-col w-full'>
            {widgetData?.lineMetricList?.map((metricData: AnalyticsMetricType, index: number) => {
              if (metricData.type !== 'LINE_DISTRIBUTION_CONFIG_TYPE') return <></>
              const items = (metricData.value as LineDistributionConfigType).distributionItems
              return <LineDistribution key={index} distributionItems={items} />
            })}
          </div>
        )}
        {Boolean(widgetData?.primaryMetricList) && widgetData?.primaryMetricList !== undefined && (
          <div className={`grid grid-cols-1 gap-6`}>
            {widgetData?.primaryMetricList?.map((primaryWidgetData: AnalyticsMetricType, index: number) => {
              return (
                <div
                  key={index}
                  className={`flex flex-col ${!Boolean(primaryWidgetData.header) ? 'py-4' : ''} gap-3 ${
                    index > 0 ? 'border-t-1 pt-4' : ''
                  } ${index + 1 === widgetData?.primaryMetricList?.length ? 'pb-2' : ''}`}
                >
                  <GenericMetricData widgetData={primaryWidgetData} type='PRIMARY' />
                </div>
              )
            })}
          </div>
        )}
        {Boolean(widgetData?.secondaryMetricsList) &&
          (widgetData?.id === 'cart-conversion' ? (
            <div className='w-7/12'>
              <div className='grid grid-cols-2 gap-6 items-center mt-12'>
                {widgetData?.secondaryMetricsList?.map((metricData: AnalyticsMetricType, index: number) => (
                  <GenericMetricData key={index} widgetData={metricData} type='SECONDARY' />
                ))}
              </div>
            </div>
          ) : (
            <div className='grid grid-cols-2 gap-6'>
              {widgetData?.secondaryMetricsList?.map((metricData: AnalyticsMetricType, index: number) => (
                <GenericMetricData key={index} widgetData={metricData} type='SECONDARY' />
              ))}
            </div>
          ))}

        {Boolean(widgetData?.detailedMetricsList) && widgetData?.detailedMetricsList !== undefined && (
          <div className='flex flex-col'>
            {widgetData?.detailedMetricsList?.map((widgetList: AnalyticsMetricType[], index: number) => {
              return (
                <div key={index} className={`flex flex-col py-4 gap-3 ${index > 0 ? 'border-t-1' : ''}`}>
                  {widgetList?.map((widgetData: AnalyticsMetricType, index: number) => {
                    return <GenericMetricData key={index} widgetData={widgetData} type='LIST' />
                  })}
                </div>
              )
            })}
          </div>
        )}
        <ComponentLoader
          isLoaderVisible={isValidating}
          message='Loading Widget...'
          customClass='border rounded border-gray-light'
          isComponent
        />
      </div>
    </div>
  )
}

export default GenericAnalyticsWidget
